import { AxisBottom, AxisLeft, TickLabelProps } from "@visx/axis";
import { GridColumns } from "@visx/grid";
import { Group } from "@visx/group";
import { scaleBand, scaleLinear } from "@visx/scale";
import { Bar } from "@visx/shape";
import { Text } from "@visx/text";

import theme from "@app/analytics/theme";
import { TrendSeriesItem } from "@app/analytics/types";

type ChartContext = "size" | "duration" | "meetingManager";

const BAR_HEIGHT = 39;

const Y_TICK_DY = 7;
const CHART_HEIGHT_MULTIPLIER = 0.8;

const CONFIG: {
  [key in ChartContext]: {
    chartAreaLeftMultiple: number;
    chartAreaWidthMultiple: number;
    tickValues: number[];
    yAxisLeft: number;
    gridColumnsColor: string;
    tickLabelProps: TickLabelProps<any>;
    labelHeight: number;
  };
} = {
  duration: {
    chartAreaLeftMultiple: 0.15,
    chartAreaWidthMultiple: 0.83,
    tickValues: [25, 50, 75],
    yAxisLeft: 8,
    gridColumnsColor: "#ffffff80",
    tickLabelProps: () => ({
      fill: theme.palette.shades.neutral[500],
      textAnchor: "start",
      fontSize: "0.75rem",
      dy: Y_TICK_DY,
    }),
    labelHeight: 5,
  },
  size: {
    chartAreaLeftMultiple: 0.15,
    chartAreaWidthMultiple: 0.83,
    tickValues: [2, 5, 16],
    yAxisLeft: 8,
    gridColumnsColor: "#ffffff80",
    tickLabelProps: () => ({
      fill: theme.palette.shades.neutral[500],
      textAnchor: "start",
      fontSize: "0.75rem",
      dy: Y_TICK_DY,
    }),
    labelHeight: 5,
  },
  meetingManager: {
    chartAreaLeftMultiple: 0.006, // just enough to fit the labels
    chartAreaWidthMultiple: 0.97,
    tickValues: [2, 5],
    yAxisLeft: 18,
    gridColumnsColor: "transparent",
    tickLabelProps: () => ({
      fill: "white",
      textAnchor: "start",
      fontSize: "0.75rem",
      fontWeight: 600,
      dy: Y_TICK_DY - 4,
    }),
    labelHeight: 0,
  },
};

const formatTick = (v: number, context: ChartContext) => {
  if (context === "duration") {
    if (v <= 30) return "<= 30 min";
    if (v <= 60) return "30-60 min";
    if (v > 60) return "> 60 min";
  } else if (context === "size") {
    if (v <= 2) return "1:1 meeting";
    if (v <= 6) return "3-6 people";
    if (v > 6) return "7+ people";
  } else if (context === "meetingManager") {
    if (v <= 2) return "Read Index";
    if (v <= 6) return "Current";
  }
};

const HorizontalBarChart = ({
  width,
  height,
  context,
  data,
  label,
}: {
  width: number;
  height: number;
  context: ChartContext;
  data: TrendSeriesItem[];
  label?: string;
}) => {
  const config = CONFIG[context];

  const chartAreaHeight = Math.max(
    0,
    height * CHART_HEIGHT_MULTIPLIER - config.labelHeight
  );
  const chartAreaWidth = width * config.chartAreaWidthMultiple;

  const yScale = scaleBand({
    domain: CONFIG[context].tickValues,
    range: [chartAreaHeight, 0],
    round: true,
  });

  const xScale = scaleLinear({
    domain: [0, 100],
    range: [0, chartAreaWidth],
  });

  return (
    <svg width={width} height={height}>
      <Group left={width * config.chartAreaLeftMultiple}>
        <rect
          width={chartAreaWidth}
          height={chartAreaHeight}
          fill={theme.palette.shades.ghost[300]}
          rx={4}
        />
        <Group top={6}>
          {data.map((d: TrendSeriesItem, i: number) => {
            const fillColor =
              context === "meetingManager" && i !== 0
                ? theme.palette.shades.primary[400]
                : theme.palette.shades.primary[500];
            return (
              <Group key={i}>
                <Bar
                  x={0}
                  y={yScale(d.y)}
                  width={xScale(d.x)}
                  height={BAR_HEIGHT}
                  fill={fillColor}
                  rx={4}
                />
              </Group>
            );
          })}
          <GridColumns
            height={chartAreaHeight}
            stroke={config.gridColumnsColor}
            scale={xScale}
            tickValues={[20, 40, 60, 80]}
          />
          {data.map((d: TrendSeriesItem, i: number) => {
            const showValueInBar = d.x > 93;
            return (
              <Group key={i}>
                <Text
                  x={xScale(d.x) + (showValueInBar ? -25 : 8)}
                  y={yScale(d.y)! + BAR_HEIGHT / 2}
                  textAnchor="start"
                  fontSize=".875rem"
                  fontWeight="500"
                  dy={5}
                  style={{
                    fill: showValueInBar
                      ? "white"
                      : theme.palette.shades.neutral[800],
                  }}
                >
                  {d.x}
                </Text>
              </Group>
            );
          })}
        </Group>
        <AxisBottom
          label={label}
          scale={xScale}
          top={chartAreaHeight + 5 - config.labelHeight}
          hideAxisLine
          hideTicks
          tickLabelProps={() => ({
            fill: theme.palette.shades.neutral[500],
            fontSize: "0.75rem",
            textAnchor: "middle",
          })}
          labelOffset={25}
          labelProps={{
            textAnchor: "middle",
            fontSize: "0.875rem",
            fill: theme.palette.shades.neutral[500],
          }}
        />
      </Group>
      <AxisLeft
        left={config.yAxisLeft}
        scale={yScale}
        hideAxisLine
        hideTicks
        tickLabelProps={config.tickLabelProps}
        tickValues={config.tickValues}
        tickFormat={(d: number) => formatTick(d, context)}
      />
    </svg>
  );
};

export default HorizontalBarChart;
